import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Badge, Box, Button, Card, CardActions, CardContent, CircularProgress, Container, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, Stack, Table, TableBody, TableCell, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Typography, styled } from "@mui/material";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Notification } from '../../types/Notification';
import { DateTime } from 'luxon';
import { Query, QueryDocumentSnapshot, QuerySnapshot, Timestamp, collection, doc, endBefore, getCountFromServer, getDoc, getDocs, getFirestore, limit, limitToLast, orderBy, query, setDoc, startAfter, startAt, where } from 'firebase/firestore';
import passportRequestConverter from '../../converters/passportRequestConverter';
import { PassportRequest } from '../../types/PassportRequest';
import { Employee } from '../../types/Employee';
import { queryEmployee } from '../../utils/queryEmployee';
import { ChatBubbleRounded, ThumbUpRounded } from '@mui/icons-material';
import { NotificationPassportRequest } from './NotificationPassportRequest';
import { useAppSelector } from '../../store';
import notificationConverter from '../../converters/notificationConverter';
import employeeConverter from '../../converters/employeeConverter';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import medicalPaperRequestConverter from '../../converters/medicalPaperRequestConverter';
import { MedicalPaperRequest } from '../../types/MedicalPaperRequest';
import { queryPassportDocumentsCountExpiresIn } from '../../utils/queryPersonalDocuments';

export const NotificationsPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const authState = useAppSelector(s => s.auth);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [status, setStatus] = useState<'All' | 'Read' | 'Unread'>('Unread');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [totalRows, setTotalRows] = useState(0);

  const [firstRow, setFirsRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const [month, setMonth] = useState(3);
  const [passportCount, setPassportCount] = useState(0);

  
  const setNotificationsList = (qSnapshot: QuerySnapshot<Notification>) =>{
    setNotifications(qSnapshot.docs.map(d => d.data()));

    setLoading(false);
  };

  const refresh = (callback?: (q: Query<Notification>) => Query<Notification>) => {
    setLoading(true);

    const colPath = `Employees/${authState.idNumber}/Notifications`;
    let q: Query<Notification> = query(collection(db, colPath), orderBy('dateCreated', 'desc')).withConverter(notificationConverter);

    if(callback)
      q = callback(q);
    if (status !== 'All')
      q = query(q, where('status', '==', status));

    getDocs(q).then(qSnapshot => {
      setNotifications(qSnapshot.docs.map(d => d.data()));
      setFirsRow(qSnapshot.docs[0]);
      setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
      setLoading(false);
    });

    // Count query
    let qCount: Query = query(collection(db, colPath));
    if(callback)
      q = callback(q);
    if (status !== 'All') 
      qCount = query(qCount, where('status', '==', status));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const handleToggleChange = (_e: React.MouseEvent<HTMLElement>, s: string | null) => {
    if (s !== null) {
      setStatus(s as 'All' | 'Read' | 'Unread');
    }
  };

  const handleNotificationItemOnClick = (n: Notification) => {
    navigate(`/home/notifications/${n.id}`, {
      state: n,
    });
  };

  const handlePageOnChange = (event: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastRow), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstRow), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstRow), limit(rowsPerPage));
      });
    }
    setPage(to);
  }
  

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    queryPassportDocumentsCountExpiresIn(3).then(c => {
      setPassportCount(c);
    });
  }, []);

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, [status, rowsPerPage]);

  return (
    <Container disableGutters>
      <Typography variant="h5" mt={2} mb={5}>Notifications</Typography>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <Stack direction='row' mb={1}>
            <Box flexGrow={1} />
            <ToggleButtonGroup value={status} size='small' exclusive onChange={handleToggleChange}>
              <ToggleButton value='All'>All</ToggleButton>
              <ToggleButton value='Unread'>Unread</ToggleButton>
              <ToggleButton value='Read'>Read</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Paper>
            <Box p={1}>
              {loading && (
                <Stack direction='row' spacing={2} p={1} alignItems='center'>
                  <CircularProgress size={24} />
                  <Typography variant='caption'>Please wait...</Typography>
                </Stack>
              )}
              {!loading && notifications.length > 0 && (
                <List>
                  {notifications.map(n => {
                    return (
                      <ListItem key={n.id} disablePadding disableGutters onClick={() => handleNotificationItemOnClick(n)} sx={{ borderRadius: theme => theme.spacing(1), overflow: 'hidden' }}>
                        <ListItemButton>
                          <ListItemText 
                            primary={
                              <Badge variant='dot' color="primary" invisible={n.status === 'Read'}>
                                <Box mr={2}>
                                  <Typography fontWeight={n.status === 'Unread' ? '700' : 'normal'}>{n.title}</Typography>
                                </Box>
                              </Badge>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography
                                  sx={{ display: 'inline' }}
                                  component="span"
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {DateTime.fromISO(n.dateCreated).toLocaleString(DateTime.DATETIME_SHORT)}
                                </Typography>
                                {` — ${n.body}`}
                              </React.Fragment>
                            } />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
              {status === 'Unread' && notifications.length === 0 && (
                <Stack p={2} direction='row' alignItems='center' spacing={2}>
                  <ThumbUpRounded color='info' sx={{ fontSize: 48 }} />
                  <Typography>Good job! You have read all notifitications.</Typography>
                </Stack>
              )}
            </Box>
          </Paper>
          {notifications.length > 0 && (
            <TablePagination
              component="div"
              rowsPerPageOptions={[8, 16, 32]}
              colSpan={5}
              count={totalRows}
              page={page}
              rowsPerPage={rowsPerPage}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
              }}
              onPageChange={handlePageOnChange}
              onRowsPerPageChange={handleRowsPerPageOnChange}
              ActionsComponent={TablePaginationActions} />
            )}
        </Grid>
        <Grid item xs={4}>
          <Stack spacing={1}>
            <Card variant='outlined'>
              <CardContent>
                <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                  Expiring Passports
                </Typography>
                <Typography variant="h5" component="div">
                  {passportCount} — {passportCount > 1 ? 'documents' : 'document' }
                </Typography>
                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                  will expire in the next {month} {month > 1 ? 'months' : 'months' }
                </Typography>
                <Typography variant="body2">
                  {passportCount > 0 ? 'Take action now!' : 'No action needed.'}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('personalDocuments')}>View</Button>
              </CardActions>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

